/**
 * API client for the operator's own choice of what a new call does while they
 * are already on a call.
 *
 * Its own file, like `lib/callAvailability.ts`: one person's call preference,
 * read by the Settings card and by any call code that needs to know it. The
 * server keeps the one copy (`crm-calls/lib/busy-mode.ts`), and the phone app
 * reads the same route.
 */
import { api } from "./api";

/** The three choices, in the order the Settings card lists them. */
export const BUSY_MODES = ["knock", "voicemail", "queue"] as const;
export type BusyMode = (typeof BUSY_MODES)[number];

/**
 * The BroadcastChannel a saved choice is announced on. The call code, often
 * in another tab that holds the phone, listens here and reads the route again,
 * so a change in Settings counts without a reload.
 */
export const BUSY_MODE_CHANNEL = "crm-busy-mode";

export function isBusyMode(value: unknown): value is BusyMode {
  return typeof value === "string" && (BUSY_MODES as readonly string[]).includes(value);
}

export function getBusyMode(): Promise<{ busyMode: BusyMode }> {
  return api.get("/admin/crm/calls/busy-mode");
}

export function setBusyMode(busyMode: BusyMode): Promise<{ busyMode: BusyMode }> {
  return api.put("/admin/crm/calls/busy-mode", { busyMode });
}

/** Tell the other CRM tabs that the choice changed. A browser without the API misses nothing important: the call code also reads the route again on focus. */
export function announceBusyMode(busyMode: BusyMode): void {
  try {
    const channel = new BroadcastChannel(BUSY_MODE_CHANNEL);
    channel.postMessage(busyMode);
    channel.close();
  } catch {
    // No BroadcastChannel here; see above.
  }
}
